import { connect } from '@ardoq/rxbeach';
import { combineLatest, map } from 'rxjs';
import { Stack } from '@ardoq/layout';
import { Header4 } from '@ardoq/typography';
import { t, type WithI18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { PRESENTATION_CHROME_CLASS } from 'presentation/chrome';
import {
  BackToSlide,
  DescriptionSidebar,
  DescriptionSidebarContent,
  SlideMarkdown,
} from './atoms';
import { slideNavigator$ } from './slideNavigator$';
import { SlideNavigatorState } from './types';
import {
  DescriptionState,
  hasDescription,
  isDescriptionShown,
} from './descriptionState';

type SlideDescriptionSidebarProps = DescriptionState &
  Pick<SlideNavigatorState, 'isNavigatorDismissed'> &
  WithI18nLocale;

const SlideDescriptionSidebarBase = ({
  isNavigatorDismissed,
  i18nLocale,
  ...descriptionState
}: SlideDescriptionSidebarProps) => {
  const { slideDescription, nodeDescription } = descriptionState;
  const isOpen =
    hasDescription(descriptionState) &&
    isDescriptionShown(descriptionState) &&
    !isNavigatorDismissed;

  return (
    <DescriptionSidebar
      $isOpen={isOpen}
      className={PRESENTATION_CHROME_CLASS}
      aria-hidden={!isOpen}
      aria-label={t(
        i18nLocale,
        'presentationAdmin:viewer.slideNavigator.slideDescriptionAriaLabel',
        'Slide description'
      )}
    >
      <DescriptionSidebarContent>
        {nodeDescription ? (
          <Stack gap="medium">
            <BackToSlide
              label={t(
                i18nLocale,
                'presentationAdmin:viewer.slideNavigator.back',
                'Back'
              )}
            />
            <Stack gap="small">
              <Header4>{nodeDescription.name}</Header4>
              {nodeDescription.text && (
                <SlideMarkdown>{nodeDescription.text}</SlideMarkdown>
              )}
            </Stack>
          </Stack>
        ) : (
          slideDescription && <SlideMarkdown>{slideDescription}</SlideMarkdown>
        )}
      </DescriptionSidebarContent>
    </DescriptionSidebar>
  );
};

const slideDescriptionSidebarViewModel$ = combineLatest([
  slideNavigator$,
  i18nLocale$,
]).pipe(
  map(
    ([
      {
        slideDescription,
        nodeDescription,
        isDescriptionExpanded,
        isNodeDescriptionExpanded,
        isNavigatorDismissed,
      },
      i18nLocale,
    ]): SlideDescriptionSidebarProps => ({
      slideDescription,
      nodeDescription,
      isDescriptionExpanded,
      isNodeDescriptionExpanded,
      isNavigatorDismissed,
      i18nLocale,
    })
  )
);

export const SlideDescriptionSidebar = connect(
  SlideDescriptionSidebarBase,
  slideDescriptionSidebarViewModel$
);
